import { motion } from "framer-motion";
import { FC } from "react";
import { useCommunityAnimations } from "@pages/community";
import { User } from "@entities/user";
import styles from "./UserList.module.scss";
import { LOCAL_STORAGE, Rating, useGetUserAvatar, Avatar } from "@shared/index";
import { Link } from "react-router-dom";
import { useExtractPhotoColors } from "@shared/hooks";

interface UserListProps {
  users: User[];
  listEndRef: React.RefObject<HTMLDivElement>;
  loading?: boolean;
  hasMoreData?: boolean;
  isInitialLoad?: boolean;
}

const roundUpToOneDecimal = (rating: number): string => {
  return (Math.ceil(rating * 10) / 10).toFixed(1);
};

interface UserListItemProps {
  user: User;
  personalUserId: string;
}

const UserListItem: FC<UserListItemProps> = ({ user, personalUserId }) => {
  const avatarUrl = useGetUserAvatar(user.photos, true);
  const { dominantColors, isLoading: colorsLoading } =
    useExtractPhotoColors(avatarUrl);

  const getInitials = (name: string) => {
    const words = name.trim().split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <Link
      to={personalUserId === user.id ? "#" : `/users/${user.id}`}
      state={{ fromUserRating: true }}
      className={styles.userCard}
    >
      <div className={styles.avatarContainer}>
        {!colorsLoading && dominantColors.length > 0 ? (
          <Avatar
            key={avatarUrl}
            src={avatarUrl}
            dominantColors={dominantColors}
            showPulseEffect={true}
          />
        ) : (
          <div className={styles.initialsAvatar}>
            {getInitials(user?.name || "")}
          </div>
        )}
        <Rating
          badges={user.badges || []}
          userId={user.id}
          isReactions={true}
        />
      </div>
      <div className={styles.userInfo}>
        <div className={styles.userName}>{user.name}</div>
      </div>
      {user.social_rating && (
        <div className={styles.followers}>
          {roundUpToOneDecimal(user.social_rating)}
        </div>
      )}
    </Link>
  );
};

export const UserList: FC<UserListProps> = (props) => {
  const { users, listEndRef, loading = false, isInitialLoad = false } = props;
  const { container } = useCommunityAnimations();

  const personalUserId = localStorage.getItem(LOCAL_STORAGE.UserId) || "";
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  return (
    <div className={styles.userListContainer}>
      <motion.div
        className={styles.communitiesList}
        variants={container}
        initial="hidden"
        animate="show"
      >
        {users.map((user: User) => (
          <motion.div
            key={user.id}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
          >
            <UserListItem user={user} personalUserId={personalUserId} />
          </motion.div>
        ))}
      </motion.div>

      {loading && isInitialLoad && (
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <span>Загрузка участников...</span>
        </div>
      )}

      {loading && !isInitialLoad && users.length > 0 && (
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <span>Загрузка участников...</span>
        </div>
      )}

      <div ref={listEndRef} className={styles.loadingTrigger} />

      {!loading && !isInitialLoad && users.length === 0 && (
        <div className={styles.emptyState}>Пользователи не найдены</div>
      )}
    </div>
  );
};
